// Form Builder field definitions, helpers, templates and country list.

export const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "f_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

export const FIELD_GROUPS = [
  {
    group: "Basic Fields",
    items: [
      { type: "short_text", label: "Short Text", icon: "Type" },
      { type: "long_text", label: "Long Text", icon: "AlignLeft" },
      { type: "number", label: "Number", icon: "Hash" },
      { type: "email", label: "Email", icon: "Mail" },
      { type: "phone", label: "Phone Number", icon: "Phone" },
      { type: "date", label: "Date", icon: "Calendar" },
      { type: "time", label: "Time", icon: "Clock" },
      { type: "dropdown", label: "Dropdown", icon: "ChevronDown" },
      { type: "multiselect", label: "Multi-select", icon: "ListChecks" },
      { type: "radio", label: "Radio Buttons", icon: "CircleDot" },
      { type: "checkbox", label: "Checkboxes", icon: "CheckSquare" },
    ],
  },
  {
    group: "Special Fields",
    items: [
      { type: "file", label: "File Upload", icon: "Paperclip" },
      { type: "file_multiple", label: "Multiple Files", icon: "Files" },
      { type: "image", label: "Image Upload", icon: "Image" },
      { type: "signature", label: "Signature", icon: "PenTool" },
      { type: "address", label: "Address", icon: "MapPinned" },
      { type: "country", label: "Country", icon: "Globe" },
      { type: "state_city", label: "State / City", icon: "Building2" },
    ],
  },
];

export const FIELD_LABELS = Object.fromEntries(
  FIELD_GROUPS.flatMap((g) => g.items).map((i) => [i.type, i.label])
);

export const OPTION_TYPES = ["dropdown", "multiselect", "radio", "checkbox"];
export const FILE_TYPES = ["file", "file_multiple", "image", "signature"];
export const TEXT_LIKE = ["short_text", "long_text", "email", "phone", "number"];

export const hasOptions = (t) => OPTION_TYPES.includes(t);
export const isFileType = (t) => FILE_TYPES.includes(t);
export const isMultiFile = (t) => t === "file_multiple";

export const ALLOWED_FILE_EXTS = ["JPG", "JPEG", "PNG", "PDF", "DOC", "DOCX"];

export function makeField(partial = {}) {
  return {
    id: uid(),
    type: "short_text",
    label: "",
    description: "",
    placeholder: "",
    required: false,
    options: [],
    config: {},
    conditional: null,
    ...partial,
  };
}

// Build fresh fields from a template spec, resolving conditional "fieldKey" refs to new ids.
export function buildTemplateFields(specs = []) {
  const keyToId = {};
  const fields = specs.map((s) => {
    const spec = { ...s };
    const key = spec.key;
    delete spec.key;
    const f = makeField(spec);
    if (key) keyToId[key] = f.id;
    return f;
  });
  fields.forEach((f) => {
    if (f.conditional && f.conditional.fieldKey) {
      f.conditional = {
        field: keyToId[f.conditional.fieldKey] || "",
        op: f.conditional.op || "equals",
        value: f.conditional.value,
      };
    }
  });
  return fields;
}

const fileCfg = (exts = ["JPG", "JPEG", "PNG", "PDF"], mb = 10) => ({
  allowed_types: exts,
  max_size_mb: mb,
});

export const TEMPLATES = [
  {
    key: "guest_registration",
    name: "Guest Registration",
    form_type: "registration",
    description: "Collect guest details, headcount and travel info.",
    specs: [
      { type: "short_text", label: "Full Name", required: true, placeholder: "Your full name" },
      { type: "email", label: "Email", required: true, placeholder: "you@example.com" },
      { type: "phone", label: "Phone Number", required: true },
      { type: "number", label: "Number of Guests", config: { min: 1, max: 20 } },
      { type: "radio", label: "Accommodation Required?", required: true, options: ["Yes", "No"], key: "accom" },
      { type: "date", label: "Arrival Date", conditional: { fieldKey: "accom", op: "equals", value: "Yes" } },
      { type: "date", label: "Departure Date", conditional: { fieldKey: "accom", op: "equals", value: "Yes" } },
      { type: "long_text", label: "Dietary Requirements", placeholder: "Allergies, preferences…" },
      { type: "file", label: "Upload Photo ID", config: fileCfg(["JPG", "JPEG", "PNG", "PDF"], 10) },
    ],
  },
  {
    key: "id_verification",
    name: "ID Verification",
    form_type: "id_verification",
    description: "Securely verify guest identity documents.",
    specs: [
      { type: "short_text", label: "Full Name", required: true },
      { type: "dropdown", label: "ID Type", required: true, options: ["Passport", "Driver's License", "National ID", "Aadhaar", "Other"] },
      { type: "short_text", label: "ID Number", required: true },
      { type: "image", label: "Photo ID — Front", required: true, config: fileCfg(["JPG", "JPEG", "PNG", "PDF"], 10) },
      { type: "image", label: "Photo ID — Back", config: fileCfg(["JPG", "JPEG", "PNG", "PDF"], 10) },
    ],
  },
  {
    key: "airport_pickup",
    name: "Airport Pickup Details",
    form_type: "airport_pickup",
    description: "Coordinate guest arrivals and transfers.",
    specs: [
      { type: "short_text", label: "Full Name", required: true },
      { type: "phone", label: "Phone Number", required: true },
      { type: "radio", label: "Pickup Required?", required: true, options: ["Yes", "No"], key: "pickup" },
      { type: "short_text", label: "Flight Number", conditional: { fieldKey: "pickup", op: "equals", value: "Yes" } },
      { type: "date", label: "Arrival Date", conditional: { fieldKey: "pickup", op: "equals", value: "Yes" } },
      { type: "time", label: "Arrival Time", conditional: { fieldKey: "pickup", op: "equals", value: "Yes" } },
      { type: "number", label: "Number of Passengers", conditional: { fieldKey: "pickup", op: "equals", value: "Yes" } },
    ],
  },
  {
    key: "accommodation",
    name: "Accommodation Details",
    form_type: "accommodation",
    description: "Manage room requirements and stay dates.",
    specs: [
      { type: "short_text", label: "Full Name", required: true },
      { type: "email", label: "Email", required: true },
      { type: "date", label: "Check-in Date", required: true },
      { type: "date", label: "Check-out Date", required: true },
      { type: "number", label: "Number of Rooms", config: { min: 1, max: 10 } },
      { type: "long_text", label: "Special Requests" },
    ],
  },
  {
    key: "sports_participant",
    name: "Sports Participant Registration",
    form_type: "sports",
    description: "Register players with medical and consent docs.",
    specs: [
      { type: "short_text", label: "Full Name", required: true },
      { type: "number", label: "Age", required: true, config: { min: 1, max: 120 } },
      { type: "short_text", label: "Team" },
      { type: "phone", label: "Contact Number", required: true },
      { type: "phone", label: "Emergency Contact", required: true },
      { type: "file", label: "Upload Photo ID", required: true, config: fileCfg(["JPG", "JPEG", "PNG", "PDF"], 10) },
      { type: "file", label: "Medical / Consent Document", config: fileCfg(["JPG", "JPEG", "PNG", "PDF", "DOC", "DOCX"], 10) },
      { type: "image", label: "Player Photo", config: fileCfg(["JPG", "JPEG", "PNG"], 10) },
    ],
  },
  {
    key: "artist_performer",
    name: "Artist / Performer Details",
    form_type: "artist",
    description: "Onboard performers with technical requirements.",
    specs: [
      { type: "short_text", label: "Artist Name", required: true },
      { type: "phone", label: "Contact", required: true },
      { type: "dropdown", label: "Performance Type", options: ["Music", "Dance", "DJ", "Comedy", "Theatre", "Other"] },
      { type: "long_text", label: "Technical Requirements" },
      { type: "file", label: "Upload Photo ID", config: fileCfg(["JPG", "JPEG", "PNG", "PDF"], 10) },
      { type: "file", label: "Contract", config: fileCfg(["PDF", "DOC", "DOCX"], 15) },
      { type: "image", label: "Profile Photo", config: fileCfg(["JPG", "JPEG", "PNG"], 10) },
    ],
  },
  {
    key: "vendor",
    name: "Vendor Registration",
    form_type: "vendor",
    description: "Register suppliers, caterers and service partners.",
    specs: [
      { type: "short_text", label: "Company Name", required: true },
      { type: "short_text", label: "Contact Person", required: true },
      { type: "email", label: "Email", required: true },
      { type: "phone", label: "Phone Number", required: true },
      { type: "dropdown", label: "Service Type", options: ["Catering", "Decor", "Photography", "Sound & Light", "Transport", "Other"] },
      { type: "short_text", label: "GST / Tax ID" },
      { type: "file_multiple", label: "Supporting Documents", config: fileCfg(["PDF", "DOC", "DOCX", "JPG", "PNG"], 15) },
    ],
  },
  {
    key: "ticket_verification",
    name: "Ticket Verification",
    form_type: "ticket",
    description: "Validate attendee tickets before entry.",
    specs: [
      { type: "short_text", label: "Full Name", required: true },
      { type: "email", label: "Email", required: true },
      { type: "short_text", label: "Ticket Number", required: true },
      { type: "file", label: "Upload Ticket", required: true, config: fileCfg(["JPG", "JPEG", "PNG", "PDF"], 10) },
    ],
  },
  {
    key: "consent",
    name: "Consent Form",
    form_type: "consent",
    description: "Capture signed consent from guests.",
    consent_enabled: true,
    specs: [
      { type: "short_text", label: "Full Name", required: true },
      { type: "email", label: "Email" },
      { type: "signature", label: "Signature", required: true },
    ],
  },
  {
    key: "custom",
    name: "Custom Guest Information",
    form_type: "custom",
    description: "Start from a blank form and add any fields.",
    specs: [
      { type: "short_text", label: "Full Name", required: true },
      { type: "email", label: "Email" },
    ],
  },
];

export function isVisible(field, answers = {}) {
  const c = field.conditional;
  if (!c || !c.field) return true;
  const actual = answers[c.field];
  const target = String(c.value ?? "").trim().toLowerCase();
  const vals = Array.isArray(actual)
    ? actual.map((a) => String(a).trim().toLowerCase())
    : actual === undefined || actual === null || actual === ""
    ? []
    : [String(actual).trim().toLowerCase()];
  const op = c.op || "equals";
  if (op === "equals") return vals.includes(target);
  if (op === "not_equals") return !vals.includes(target);
  if (op === "contains") return vals.some((v) => v.includes(target));
  return true;
}

export const COUNTRIES = [
  "India", "United States", "United Kingdom", "Canada", "Australia", "United Arab Emirates",
  "Singapore", "Germany", "France", "Italy", "Spain", "Netherlands", "Switzerland", "Sweden",
  "Norway", "Denmark", "Ireland", "Portugal", "Belgium", "Austria", "New Zealand", "Japan",
  "China", "South Korea", "Hong Kong", "Malaysia", "Thailand", "Indonesia", "Philippines",
  "Vietnam", "Sri Lanka", "Nepal", "Bangladesh", "Pakistan", "Saudi Arabia", "Qatar", "Kuwait",
  "Bahrain", "Oman", "Israel", "Turkey", "Egypt", "South Africa", "Nigeria", "Kenya", "Ghana",
  "Brazil", "Mexico", "Argentina", "Chile", "Colombia", "Peru", "Russia", "Poland", "Greece",
  "Czech Republic", "Hungary", "Romania", "Finland", "Other",
];
